import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ExternalLink, Eye, EyeOff, Layers, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type BulkAction,
  type RowAction,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type {
  BulkCollectionsRequest,
  BulkResponse,
  Collection,
  CollectionStatus,
} from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { openStorefrontUrl } from '@/features/stores';
import { BulkResultDialog } from '@/shared/bulk/bulk-result-dialog';
import { useStore } from '@/shared/store/store-context';

import { useCollectionColumns } from '../components/collection-columns';
import { collectionFilters } from '../components/collection-filters';
import { DeleteCollectionDialog } from '../components/delete-collection-dialog';
import { useBulkCollections, useUpdateCollection } from '../hooks/use-collection-mutations';
import { useCollections } from '../hooks/use-collections';

const ROUTE_ID = '/_authed/_active/s/$storeId/collections/' as const;
const ROUTE_PATH = '/s/$storeId/collections/' as const;

function NewCollectionButton({ storeId, tour }: { storeId: string; tour?: string }) {
  const { t } = useTranslation('collections');
  return (
    <Can permission="collections.write">
      <Button asChild data-tour={tour}>
        <Link to="/s/$storeId/collections/new" params={{ storeId }}>
          <Plus aria-hidden />
          {t('new')}
        </Link>
      </Button>
    </Can>
  );
}

function Collections() {
  const { t } = useTranslation(['collections', 'common']);
  const store = useStore();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const message = useErrorMessage();
  const { can } = usePermissions();
  const canWrite = can('collections.write');
  const update = useUpdateCollection(store.id);
  const [deleting, setDeleting] = useState<Collection | null>(null);
  const bulk = useBulkCollections(store.id);
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null);
  const [result, setResult] = useState<BulkResponse | null>(null);
  const [titles, setTitles] = useState<ReadonlyMap<string, string>>(new Map());

  const filters = useMemo(() => collectionFilters(t), [t]);
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useCollections(store.id, list.params);

  const mutateStatus = update.mutate;
  const setStatus = useCallback(
    (row: Collection, status: CollectionStatus) =>
      mutateStatus(
        { id: row.id, body: { status } },
        { onError: (error) => toast.error(message(error)) },
      ),
    [message, mutateStatus],
  );

  const open = useCallback(
    (row: Collection) =>
      void navigate({
        to: '/s/$storeId/collections/$collectionId',
        params: { storeId: store.id, collectionId: row.id },
      }),
    [navigate, store.id],
  );

  const rowActions = useMemo<Array<RowAction<Collection>>>(
    () => [
      { id: 'open', label: t('actions.open'), icon: <Pencil aria-hidden />, onAction: open },
      {
        id: 'view',
        label: t('actions.view'),
        icon: <ExternalLink aria-hidden />,
        hidden: (row) => !row.storefrontUrl || row.status !== 'active',
        onAction: (row) => openStorefrontUrl(row.storefrontUrl),
      },
      {
        id: 'hide',
        label: t('actions.hide'),
        icon: <EyeOff aria-hidden />,
        separatorBefore: true,
        hidden: (row) => !canWrite || row.status !== 'active',
        onAction: (row) => setStatus(row, 'hidden'),
      },
      {
        id: 'show',
        label: t('actions.show'),
        icon: <Eye aria-hidden />,
        separatorBefore: true,
        hidden: (row) => !canWrite || row.status === 'active',
        onAction: (row) => setStatus(row, 'active'),
      },
      {
        id: 'delete',
        label: t('actions.delete'),
        icon: <Trash2 aria-hidden />,
        variant: 'destructive',
        separatorBefore: true,
        hidden: () => !canWrite,
        onAction: (row) => setDeleting(row),
      },
    ],
    [canWrite, open, setStatus, t],
  );

  const mutateBulk = bulk.mutate;
  /** A 207 opens the per-row result; a failed delete stays in its confirm dialog. */
  const runBulk = useCallback(
    (body: BulkCollectionsRequest) =>
      mutateBulk(body, {
        onSuccess: (response) => {
          setConfirmDelete(null);
          if (response.failed > 0) setResult(response);
          else toast.success(t('bulk.done', { count: response.succeeded }));
        },
        onError: (error) => {
          if (body.action !== 'delete') toast.error(message(error));
        },
      }),
    [message, mutateBulk, t],
  );

  const bulkActions = useMemo<Array<BulkAction<Collection>>>(() => {
    if (!canWrite) return [];
    const remember = (rows: readonly Collection[]) => {
      setTitles(new Map(rows.map((row) => [row.id, row.title])));
      return rows.map((row) => row.id);
    };
    const setStatusOf = (
      status: CollectionStatus,
      label: string,
      icon: React.ReactNode,
    ): BulkAction<Collection> => ({
      id: `status-${status}`,
      label,
      icon,
      promoted: true,
      onAction: (_selection, rows) => {
        if (rows.length === 0) return;
        runBulk({ ids: remember(rows), action: 'set_status', status });
      },
    });
    return [
      setStatusOf('active', t('bulk.show'), <Eye aria-hidden />),
      setStatusOf('hidden', t('bulk.hide'), <EyeOff aria-hidden />),
      {
        id: 'delete',
        label: t('bulk.delete'),
        icon: <Trash2 aria-hidden />,
        variant: 'destructive',
        onAction: (_selection, rows) => {
          if (rows.length === 0) return;
          bulk.reset();
          setConfirmDelete(remember(rows));
        },
      },
    ];
  }, [bulk, canWrite, runBulk, t]);

  const columns = useCollectionColumns(store.id);
  const rows = query.data?.items ?? [];
  const table = useDataTable({
    mode: 'server',
    data: rows,
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'collections',
  });

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && rows.length === 0 && !narrowed;

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        actions={<NewCollectionButton storeId={store.id} tour="collections.list.add-button" />}
      />
      {isEmptyList ? (
        <EmptyState
          icon={Layers}
          title={t('empty.title')}
          description={t('empty.description')}
          action={<NewCollectionButton storeId={store.id} />}
          tour="collections.list.empty"
          tourLink
        />
      ) : (
        <DataTable
          table={table}
          tour={{ table: 'collections.list.table', filters: 'collections.list.filters' }}
          search={{ placeholder: t('search'), label: t('search') }}
          loading={query.isPending}
          fetching={query.isFetching || update.isPending || bulk.isPending}
          error={query.error}
          onRetry={() => void query.refetch()}
          rowActions={rowActions}
          bulkActions={bulkActions}
          onRowClick={open}
          getRowLabel={(row) => row.title}
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('noMatches') }}
        />
      )}
      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(next) => {
          if (!next) setConfirmDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('bulk.deleteTitle', { count: confirmDelete?.length ?? 0 })}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('bulk.deleteDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          {bulk.isError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{message(bulk.error)}</AlertDescription>
            </Alert>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulk.isPending}>
              {t('common:actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={bulk.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (confirmDelete) runBulk({ ids: confirmDelete, action: 'delete' });
              }}
            >
              {bulk.isPending ? t('bulk.working') : t('bulk.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <BulkResultDialog
        result={result}
        titles={titles}
        fallbackTitle={(id) => t('bulk.result.unknown', { id })}
        description={t('bulk.result.description')}
        onClose={() => setResult(null)}
      />
      <DeleteCollectionDialog
        storeId={store.id}
        row={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

export function CollectionsPage() {
  return (
    <FeatureErrorBoundary name="collections.list">
      <Collections />
    </FeatureErrorBoundary>
  );
}
